import { type TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandS } from '@sorare/core/src/atoms/typography';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary } from '@sorare/core/src/lib/glossary';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import type { PromoBanner_conversionCredit } from './__generated__/index.graphql';
import background from './assets/background.png';

const Root = styled(Horizontal).attrs({ gap: 3 })`
  --border-gradient: var(--c-gradient-discount);
  --border-size: 2px;

  width: 100%;
  ${borderGradientStyle};
  padding: var(--double-unit);
  border-radius: var(--double-and-a-half-unit);
  background:
    no-repeat center / cover url(${background}),
    var(--c-nd-50-opaque);
`;
const StyledImg = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;
`;

type Props = {
  conversionCredit: Nullable<PromoBanner_conversionCredit>;
  leftAligned?: boolean;
};
export const PromoBanner = ({ conversionCredit, leftAligned }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { maxDiscount, percentageDiscount } = conversionCredit || {};
  const { main } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(maxDiscount),
  });

  if (!conversionCredit) return null;

  return (
    <Root center={!leftAligned}>
      <Vertical gap={0.5} center>
        <StyledImg src={`${FRONTEND_ASSET_HOST}/rewards/credit.png`} alt="" />
        <BrandS>
          <FormattedMessage
            {...glossary.percent}
            values={{ percent: percentageDiscount }}
          />
        </BrandS>
      </Vertical>
      <Vertical>
        <BrandS>
          <FormattedMessage
            id="PromoBanner.title"
            defaultMessage="Welcome Promo"
          />
        </BrandS>
        <BodyS color="var(--c-nd-700)">
          <FormattedMessage
            id="PromoBanner.description"
            defaultMessage="Get up to <b>{percentageDiscount, number, percent}</b> off your first purchase. Value up to {limit}."
            values={{
              percentageDiscount,
              limit: maxDiscount ? main : 0,
              b: (chunks: ReactNode[]) => {
                return (
                  <BodyS as="span" color="var(--c-white)" bold>
                    {chunks}
                  </BodyS>
                );
              },
            }}
          />
        </BodyS>
      </Vertical>
    </Root>
  );
};

PromoBanner.fragments = {
  conversionCredit: gql`
    fragment PromoBanner_conversionCredit on ConversionCredit {
      id
      percentageDiscount
      maxDiscount {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<PromoBanner_conversionCredit>,
};
